import { Link } from "@tanstack/react-router";
import { useOrganizationList } from "@clerk/react";
import { MolduraEntrada } from "@/components/entrada/MolduraEntrada";
import { CartaoEntrada } from "@/components/entrada/CartaoEntrada";
import { Spinner } from "@/components/ui/spinner";
import { AtivarClinica } from "./AtivarClinica";
import { CriarClinica } from "./CriarClinica";

/**
 * O passo entre entrar e usar: qual é a sua clínica.
 *
 * Quem já é membro de alguma clínica e só está sem uma ATIVA é o convidado que
 * acabou de criar a conta: ativa a mais antiga, em vez de oferecer "crie a sua"
 * — senão ele viraria dono de uma clínica vazia, fora da que o convidou.
 */
export function Onboarding({ nova = false }: { nova?: boolean }) {
  const { isLoaded, userMemberships } = useOrganizationList({ userMemberships: { infinite: true } });

  // Pedido de dentro do painel: a pessoa JÁ tem clínica e quer mais uma. Nada
  // de "entrar na clínica que te convidou" — é criar, e poder desistir.
  if (nova) {
    return (
      <MolduraEntrada nota="Crie o espaço de outra clínica. Você continua com acesso às que já tem, e alterna entre elas pelo seletor no topo do painel.">
        <CartaoEntrada rotulo="Nova clínica" titulo="Nome da clínica" descricao="Como os pacientes e a equipe conhecem esse espaço.">
          <CriarClinica />
          <p className="mt-4 text-center text-xs text-muted-foreground">
            <Link to="/conversas" className="font-semibold text-primary hover:underline">
              Voltar para o painel
            </Link>
          </p>
        </CartaoEntrada>
      </MolduraEntrada>
    );
  }

  if (!isLoaded || userMemberships.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="text-primary" />
      </div>
    );
  }

  const convidada = [...(userMemberships.data ?? [])].sort(
    (a, b) => new Date(a.organization.createdAt).getTime() - new Date(b.organization.createdAt).getTime(),
  )[0]?.organization;

  if (convidada) {
    return (
      <MolduraEntrada nota="Conectando você ao espaço da clínica para a qual você foi convidado.">
        <AtivarClinica orgId={convidada.id} nome={convidada.name} />
      </MolduraEntrada>
    );
  }

  return (
    <MolduraEntrada nota="Crie o espaço da sua clínica para começar a centralizar o atendimento, agendar pacientes e integrar com seu WhatsApp.">
      <CartaoEntrada rotulo="Primeiro passo" titulo="Nome da sua clínica" descricao="Como os pacientes e sua equipe conhecem o seu espaço.">
        <CriarClinica />
      </CartaoEntrada>
    </MolduraEntrada>
  );
}
